import { useFilterParams } from "@/hooks/useFilterParams"
import { usePagination } from "@/hooks/usePagination"
import { ConfigProvider, Pagination, PaginationProps } from "antd"
import { FC, useState } from "react"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { capitalizeFirstLetter } from "src/utils"
import { useAuth } from "../../../components/auth"
import { useGetRegionsQuery, useGetUserNamesQuery } from "../../../graphql"
import CompetitionList from "./CompetitionList"
import GlobalList from "./GlobalList"
import Filter from "./components/Filter"
import "./index.less"

const Leaderboard: FC<LeaderboardsInterface> = ({ leaderboardType }) => {
  const { region } = useAuth()

  const { page, pageSize, setPage, setPageSize } = usePagination()

  const [totalItems, setTotalItems] = useState<number>(0)

  //year options
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: currentYear - 2023 + 1 }, (_, i) => ({
    label: String(currentYear - i),
    key: String(i),
  }))

  //month options
  const currentMonth = new Date().getMonth() + 1
  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    return {
      label: capitalizeFirstLetter(new Date(currentYear, index).toLocaleString("en-US", { month: "long" })),
      key: String(index + 1),
    }
  })
  const currentMonthName = monthOptions?.find(option => +option.key == currentMonth)!.label

  const DEFAULT_FILTERS = {
    selectedDSC: "All",
    selectedRegion: region?.name || "North America",
    selectedMonth: currentMonthName!,
    selectedYear: String(currentYear),
    sortBy: "Most Units Sold",
  }

  const { filters, setFilters, filtersKey } = useFilterParams(DEFAULT_FILTERS)

  //sorting options
  const sortOptions = [
    { label: "Most Units Sold", value: "quantity", key: "1" },
    { label: "Most Deals / Sets Sold", value: "deals", key: "2" },
  ]

  //region options
  const { data: regionsData } = useGetRegionsQuery()
  const regionOptions = Array.from({ length: regionsData?.regions?.data.length || 0 }, (_, i) => ({
    label: regionsData?.regions?.data[i].attributes!.name!,
    value: regionsData?.regions?.data[i].attributes!.name!,
    key: regionsData?.regions?.data[i].id!,
  }))

  //dsc options
  const { data: dscData } = useGetUserNamesQuery({
    variables: {
      filter: {
        dsc: {
          sales_manager: {
            distributor: {
              region: {
                name: { eq: filters.selectedRegion },
              },
            },
          },
        },
      },
      pagination: { limit: 10000, start: 0 },
    },
  })
  const dscOptions = Array.from({ length: dscData?.usersPermissionsUsers?.data.length || 0 }, (_, i) => ({
    label: `${dscData?.usersPermissionsUsers?.data[i]?.attributes?.name}${dscData?.usersPermissionsUsers?.data[i]?.attributes?.blocked ? " (Blocked)" : ""}`,
    value: dscData?.usersPermissionsUsers?.data[i].attributes!.name!,
    key: dscData?.usersPermissionsUsers?.data[i].attributes!.email!,
  }))

  //available months per selected year
  const availableMonthOptions = monthOptions?.filter(
    (month, i) => +filters.selectedYear !== currentYear || i < currentMonth
  )

  const onChangePage: PaginationProps["onChange"] = pageNumber => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize) => {
    setPageSize(pageSize)
  }

  const listProps = {
    page,
    setPage,
    filters,
    filtersKey,
    pageSize,
    setFilters,
    dscOptions,
    sortOptions,
    monthOptions,
    setTotalItems,
  }

  const filterProps = {
    filters,
    totalItems,
    setFilters,
    currentYear,
    yearOptions,
    sortOptions,
    regionOptions,
    leaderboardType,
    currentMonthName,
    availableMonthOptions,
  }

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition key={leaderboardType} timeout={0} classNames={"fade"} appear={true} unmountOnExit>
        <section className={"leaderboards"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
              },
            }}
          >
            <Filter {...filterProps} />
            {leaderboardType == "global" ? <GlobalList {...listProps} /> : <CompetitionList {...listProps} />}
            <div className={"navigation-wrapper"}>
              <div
                className={"navigation"}
                children={
                  <Pagination
                    showQuickJumper
                    current={page}
                    pageSize={pageSize}
                    total={totalItems}
                    onChange={onChangePage}
                    onShowSizeChange={onChangePageSize}
                  />
                }
              />
            </div>
          </ConfigProvider>
        </section>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default Leaderboard
